"use client";

import React from "react";
import * as Sentry from "@sentry/nextjs";

const CHUNK_RELOAD_KEY = "chunk_error_reload_count";
const MAX_RELOADS = 1;

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error("ErrorBoundary caught error:", error);
    return { hasError: true, error };
  }

  componentDidUpdate(
    prevProps: { children: React.ReactNode },
    prevState: { hasError: boolean; error?: Error }
  ) {
    if (!prevState.hasError && this.state.hasError && !this.state.error) {
      Sentry.captureMessage(
        "ErrorBoundary: hasError=true but error is missing (probable SSR/hydration error)",
        { level: "error" }
      );
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      // Handle chunk loading errors with auto-reload
      if (
        error.message.includes("Failed to load chunk") ||
        error.message.includes("Loading chunk")
      ) {
        const reloadCount = parseInt(
          sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0",
          10
        );

        if (reloadCount < MAX_RELOADS) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
          window.location.reload();
          return; // Don't capture in Sentry if we're auto-reloading
        } else {
          // Reset counter after 30 seconds
          setTimeout(() => {
            sessionStorage.removeItem(CHUNK_RELOAD_KEY);
          }, 30000);
        }
      }

      Sentry.captureException(error, {
        tags: {
          errorBoundary: true,
        },
        extra: {
          componentStack: errorInfo.componentStack,
          errorBoundaryInfo: errorInfo,
        },
      });
    } catch (sentryError) {
      console.error("Failed to send to Sentry:", sentryError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, textAlign: "center" }}>
          <h2>Niečo sa pokazilo</h2>
          <p style={{ margin: 10 }}>Nastala chyba pri načítavaní stránky.</p>
          <button
            onClick={() => {
              sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              window.location.reload();
            }}
            style={{
              margin: 10,
              padding: "10px 20px",
              background: "#5B9BD5",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Obnoviť stránku
          </button>
          {process.env.NODE_ENV === "development" && (
            <details style={{ marginTop: 20, textAlign: "left" }}>
              <summary>Detaily chyby (iba pre vývojárov)</summary>
              <pre
                style={{
                  background: "#f5f5f5",
                  padding: 10,
                  borderRadius: 4,
                  overflow: "auto",
                }}
              >
                {this.state.error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
